import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
	ArrowLeft,
	Play,
	Pause,
	RotateCcw,
	SkipForward,
	MessageSquare,
	Image as ImageIcon,
	StickyNote,
	DoorOpen,
	CircleCheck,
	Clock,
	Loader2,
	Gauge,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const TIPOS = {
	open: {
		icone: DoorOpen,
		classe: "bg-primary/10 text-primary",
		lado: "centro",
	},
	close: {
		icone: CircleCheck,
		classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
		lado: "centro",
	},
	note: {
		icone: StickyNote,
		classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
		lado: "centro",
	},
	media: {
		icone: ImageIcon,
		classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
		lado: null,
	},
	message: {
		icone: MessageSquare,
		classe: "bg-muted text-muted-foreground",
		lado: null,
	},
};

const VELOCIDADES = [1, 2, 4];

// A espera real vira uma pausa curta: o replay mostra onde demorou sem
// obrigar quem assiste a esperar o mesmo tanto.
const duracaoDoPasso = (segundos, velocidade) => {
	const espera = Math.min(Math.max(segundos || 0, 0), 300);
	const ms = 700 + (espera / 300) * 1800;

	return ms / velocidade;
};

const formatarEspera = (segundos) => {
	if (!segundos || segundos < 60) return `${segundos || 0}s`;
	if (segundos < 3600) return `${Math.round(segundos / 60)} min`;

	const horas = Math.floor(segundos / 3600);
	const minutos = Math.round((segundos % 3600) / 60);

	return minutos ? `${horas}h ${minutos}min` : `${horas}h`;
};

const TicketReplay = () => {
	const { ticketId } = useParams();
	const navigate = useNavigate();

	const [dados, setDados] = useState(null);
	const [carregando, setCarregando] = useState(true);

	const [posicao, setPosicao] = useState(0);
	const [tocando, setTocando] = useState(false);
	const [velocidade, setVelocidade] = useState(2);

	const fimRef = useRef(null);

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get(`/tickets/${ticketId}/replay`);
			setDados(data);
			// Começa no primeiro passo já visível, senão a tela abre vazia.
			setPosicao(data?.passos?.length ? 1 : 0);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [ticketId]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const passos = useMemo(() => dados?.passos || [], [dados]);

	// O avanço automático: um timer por passo, com a duração do próprio passo.
	useEffect(() => {
		if (!tocando || posicao >= passos.length) return undefined;

		const proximo = passos[posicao];
		const timer = setTimeout(
			() => setPosicao((p) => p + 1),
			duracaoDoPasso(proximo?.segundosDesdeAnterior, velocidade)
		);

		return () => clearTimeout(timer);
	}, [tocando, posicao, passos, velocidade]);

	useEffect(() => {
		if (posicao >= passos.length && passos.length) setTocando(false);
	}, [posicao, passos.length]);

	useEffect(() => {
		if (tocando) fimRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
	}, [posicao, tocando]);

	if (carregando) {
		return (
			<MainContainer>
				<div className="flex flex-1 items-center justify-center">
					<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	const { ticket, resumo = {} } = dados || {};

	const noFim = posicao >= passos.length;
	const progresso = passos.length ? (posicao / passos.length) * 100 : 0;
	const visiveis = passos.slice(0, posicao);

	const alternar = () => {
		if (noFim) setPosicao(0);
		setTocando((t) => !t);
	};

	const reiniciar = () => {
		setPosicao(0);
		setTocando(true);
	};

	return (
		<MainContainer>
			<div className="flex items-center gap-2 border-b px-4 py-3 sm:px-6">
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 shrink-0"
					aria-label="Voltar"
					onClick={() => navigate(-1)}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div className="min-w-0 flex-1">
					<h1 className="truncate text-lg font-semibold tracking-tight">
						Replay do atendimento #{ticket?.id}
					</h1>
					<p className="truncate text-xs text-muted-foreground">
						{ticket?.contato}
						{ticket?.atendente && ` · atendido por ${ticket.atendente}`}
					</p>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="hidden h-8 shrink-0 sm:inline-flex"
					onClick={() => navigate(`/tickets/${ticket?.id}`)}
				>
					Abrir conversa
				</Button>
			</div>

			{/* Os números do atendimento inteiro, antes de assistir. */}
			<div className="grid grid-cols-2 gap-px border-b bg-border sm:grid-cols-4">
				{[
					{ rotulo: "Passos", valor: resumo.totalPassos },
					{ rotulo: "Mensagens", valor: resumo.mensagens },
					{
						rotulo: "Resposta média",
						valor: formatarEspera(resumo.tempoMedioRespostaSegundos),
					},
					{
						rotulo: "Duração",
						valor: formatarEspera((resumo.duracaoTotalMinutos || 0) * 60),
					},
				].map((item) => (
					<div key={item.rotulo} className="bg-card px-4 py-3">
						<p className="text-[11px] uppercase tracking-wide text-muted-foreground">
							{item.rotulo}
						</p>
						<p className="text-lg font-semibold tabular-nums">{item.valor}</p>
					</div>
				))}
			</div>

			<div className="flex-1 overflow-auto p-4 sm:p-6">
				{passos.length === 0 ? (
					<p className="py-16 text-center text-sm text-muted-foreground">
						Este atendimento não tem nada registrado para reproduzir.
					</p>
				) : (
					<ol className="mx-auto max-w-2xl space-y-3">
						{visiveis.map((passo, indice) => {
							const tipo = TIPOS[passo.tipo] || TIPOS.message;
							const Icone = tipo.icone;
							const doAtendente = passo.titulo === "Atendente respondeu";
							const centralizado = tipo.lado === "centro";
							const atual = indice === visiveis.length - 1 && tocando;

							return (
								<li key={indice}>
									{/* A espera entre um passo e outro é o que o replay revela. */}
									{passo.segundosDesdeAnterior > 60 && (
										<p className="my-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
											<Clock className="h-3 w-3" />
											{formatarEspera(passo.segundosDesdeAnterior)} de espera
										</p>
									)}

									<div
										className={cn(
											"flex gap-2",
											centralizado && "justify-center",
											!centralizado && doAtendente && "justify-end"
										)}
									>
										<div
											className={cn(
												"max-w-[85%] rounded-2xl border px-3 py-2 transition-shadow",
												centralizado
													? "bg-muted/40 text-center"
													: doAtendente
													? "bg-primary/10"
													: "bg-card",
												atual && "ring-2 ring-primary/40"
											)}
										>
											<div className="flex items-center gap-1.5">
												<span
													className={cn(
														"flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
														tipo.classe
													)}
												>
													<Icone className="h-3 w-3" />
												</span>
												<p className="text-xs font-medium">{passo.titulo}</p>
												<time className="ml-auto shrink-0 pl-2 text-[10px] tabular-nums text-muted-foreground">
													{format(parseISO(passo.data), "HH:mm")}
												</time>
											</div>

											{passo.descricao && (
												<p className="mt-1 whitespace-pre-line wrap-break-word text-sm leading-relaxed">
													{passo.descricao}
												</p>
											)}

											{passo.meta?.mediaUrl &&
												passo.meta.mediaType === "image" && (
													<img
														src={passo.meta.mediaUrl}
														alt=""
														className="mt-2 max-h-40 rounded-lg object-cover"
													/>
												)}

											{passo.autor && (
												<p className="mt-1 text-[10px] text-muted-foreground">
													{passo.autor}
												</p>
											)}
										</div>
									</div>
								</li>
							);
						})}
						<li ref={fimRef} />
					</ol>
				)}
			</div>

			{/* Controles fixos no rodapé: é a parte que a pessoa procura. */}
			{passos.length > 0 && (
				<div className="border-t bg-card px-4 py-3 sm:px-6">
					<div
						className="mx-auto mb-3 h-1.5 max-w-2xl cursor-pointer rounded-full bg-muted"
						role="progressbar"
						aria-label="Progresso do replay"
						aria-valuenow={Math.round(progresso)}
						aria-valuemin={0}
						aria-valuemax={100}
						onClick={(e) => {
							const barra = e.currentTarget.getBoundingClientRect();
							const fracao = (e.clientX - barra.left) / barra.width;
							setPosicao(
								Math.min(
									passos.length,
									Math.max(1, Math.round(fracao * passos.length))
								)
							);
						}}
					>
						<div
							className="h-full rounded-full bg-primary transition-[width] duration-300"
							style={{ width: `${progresso}%` }}
						/>
					</div>

					<div className="mx-auto flex max-w-2xl items-center gap-2">
						<Button
							size="icon"
							className="h-10 w-10 shrink-0 rounded-full"
							aria-label={tocando ? "Pausar" : "Reproduzir"}
							onClick={alternar}
						>
							{tocando ? (
								<Pause className="h-4 w-4" />
							) : (
								<Play className="h-4 w-4" />
							)}
						</Button>

						<Button
							variant="ghost"
							size="icon"
							className="h-9 w-9 shrink-0"
							aria-label="Começar de novo"
							onClick={reiniciar}
						>
							<RotateCcw className="h-4 w-4" />
						</Button>

						<Button
							variant="ghost"
							size="icon"
							className="h-9 w-9 shrink-0"
							aria-label="Ir para o fim"
							onClick={() => {
								setTocando(false);
								setPosicao(passos.length);
							}}
						>
							<SkipForward className="h-4 w-4" />
						</Button>

						<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
							{Math.min(posicao, passos.length)}/{passos.length}
						</span>

						<div className="ml-auto flex shrink-0 items-center gap-1 rounded-full border p-0.5">
							<Gauge className="ml-1.5 h-3.5 w-3.5 text-muted-foreground" />
							{VELOCIDADES.map((v) => (
								<button
									key={v}
									type="button"
									aria-label={`Velocidade ${v}x`}
									aria-pressed={velocidade === v}
									className={cn(
										"rounded-full px-2 py-1 text-xs font-medium tabular-nums transition-colors",
										velocidade === v
											? "bg-primary text-primary-foreground"
											: "text-muted-foreground hover:bg-muted"
									)}
									onClick={() => setVelocidade(v)}
								>
									{v}x
								</button>
							))}
						</div>
					</div>
				</div>
			)}
		</MainContainer>
	);
};

export default TicketReplay;
